"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

const HOVER_DWELL_MS = 100;
const SCROLL_SETTLE_MS = 150;

/*
 * Dedupe only; no cap needed. The payloads live in Next.js's client cache, which
 * bounds memory itself (50 MB LRU, 5-min TTL).
 */
const prefetched = new Set<string>();

/* Scrolling slides cards under a stationary cursor; those mouseenters aren't intent. */
let lastScrollAt = 0;
let trackingScroll = false;
function ensureScrollTracking() {
  if (trackingScroll) return;
  trackingScroll = true;
  /* Capture phase so scrolls inside nested containers (e.g. scroll areas) count too. */
  window.addEventListener("scroll", () => (lastScrollAt = Date.now()), { passive: true, capture: true });
}

const isScrolling = () => Date.now() - lastScrollAt < SCROLL_SETTLE_MS;

/*
 * Experiment: hovering a card for HOVER_DWELL_MS prefetches the profile route.
 * router.prefetch() fully renders the dynamic page server-side and keeps the
 * payload in the client cache (staleTimes.static, 5 min default), so the click
 * navigation is instant. Next.js only prefetches in production builds.
 */
export function usePrefetchOnHover(href: string | undefined) {
  const router = useRouter();
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    ensureScrollTracking();
    return () => clearTimeout(timer.current);
  }, []);

  if (!href) return {};

  const onMouseEnter = () => {
    if (isScrolling() || prefetched.has(href)) return;
    timer.current = setTimeout(() => {
      if (isScrolling() || prefetched.has(href)) return;
      prefetched.add(href);
      router.prefetch(href);
    }, HOVER_DWELL_MS);
  };

  const onMouseLeave = () => clearTimeout(timer.current);

  return { onMouseEnter, onMouseLeave };
}
